import { useEffect, useState } from 'react';

import {
	FIRST_TIME_USER_DAYS,
	TRAIT_CONFLUENCE_FIRST_ACTIVE,
	TRAIT_HAS_SENT_ROVO_CHAT_MESSAGE,
	TRAIT_IS_ROVO_CHAT_MAU,
	TRAIT_JIRA_FIRST_ACTIVE,
	TRAITS_FETCH_TIMEOUT_MS,
} from '../../common/constants';
import { type RovoReintroductionProduct } from '../../types';

type TraitValue = string | number | boolean | null;

type Trait = {
	name: string;
	value: TraitValue;
};

type PersonalizationResponse = {
	attributes?: Trait[];
};

/** The product activation-date trait whose recency marks a first-time user. */
const FIRST_ACTIVE_TRAIT_BY_PRODUCT: Record<RovoReintroductionProduct, string> = {
	jira: TRAIT_JIRA_FIRST_ACTIVE,
	confluence: TRAIT_CONFLUENCE_FIRST_ACTIVE,
};

const getUserTraitsUrl = (userId: string): string =>
	`/gateway/api/tap-delivery/api/v3/personalization/user/${encodeURIComponent(userId)}`;

const getSiteUserTraitsUrl = (cloudId: string, userId: string): string =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${encodeURIComponent(
		cloudId,
	)}/user/${encodeURIComponent(userId)}`;

async function fetchTraits(url: string, signal: AbortSignal): Promise<Trait[]> {
	const response = await fetch(url, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			// MAU is tracked server-side; this client read should not count as activity.
			'x-atlassian-mau-ignore': 'true',
		},
		signal,
	});

	if (!response.ok) {
		throw new Error(`TAP request failed with status ${response.status}`);
	}

	const json = (await response.json()) as PersonalizationResponse;
	return json.attributes ?? [];
}

/**
 * Coerces a TAP trait value to a boolean. TAP may deliver booleans as real
 * booleans or as the strings `"true"`/`"false"`, so handle both. A missing trait
 * is treated as falsy.
 */
function isTraitTruthy(value: TraitValue | undefined): boolean {
	return value === true || value === 'true' || value === 1 || value === '1';
}

/**
 * True when the given activation date is within the first {@link FIRST_TIME_USER_DAYS}
 * days, i.e. the user is still in their onboarding window. A missing or
 * unparseable activation date is treated as first-time (most conservative: a
 * user we cannot confirm as established is assumed to be new), matching the
 * existing `cross-join-nudges` "user too new" precedent.
 */
function isFirstTimeUser(activationDate: TraitValue | undefined): boolean {
	if (typeof activationDate !== 'string') {
		return true;
	}
	const activation = new Date(activationDate);
	if (Number.isNaN(activation.getTime())) {
		return true;
	}
	const threshold = new Date(activation);
	threshold.setDate(activation.getDate() + FIRST_TIME_USER_DAYS);
	return new Date() <= threshold;
}

export type RovoChatEligibility = {
	/**
	 * True when the user qualifies for the reintroduction modal: they have NOT
	 * sent a Rovo chat message OR they are dormant (not a Rovo chat MAU).
	 */
	eligible: boolean;
	/** True once the TAP fetch has settled (success or failure). */
	loaded: boolean;
};

const DISABLED_RESULT: RovoChatEligibility = { loaded: false, eligible: false };

export type UseRovoChatEligibilityOptions = {
	/**
	 * Current site cloud ID. Required to read the site-scoped activation-date
	 * trait. When omitted, first-time-user suppression is skipped (the modal
	 * targets purely on the Rovo-chat traits, preserving legacy behaviour).
	 */
	cloudId?: string;
	enabled?: boolean;
	/** Host product, used to pick the activation-date trait for first-time detection. */
	product: RovoReintroductionProduct;
};

/**
 * Resolves client-side targeting for the Rovo reintroduction modal.
 *
 * A user qualifies on Rovo-chat behaviour (two TAP user-level traits OR'd):
 *
 * - `segment_has_sent_rovo_chat_message_aaid` falsy → user has never sent a
 *   Rovo chat message.
 * - `is_rovo_chat_mau` falsy → user is dormant.
 *
 * First-time users are then excluded: when a `cloudId` is provided, the
 * site-scoped product activation-date trait (`<product>_first_active`) is read
 * and any user still within their first {@link FIRST_TIME_USER_DAYS} days is
 * suppressed, because they are covered by a dedicated onboarding experience.
 *
 * If the fetch fails or times out, the user is treated as not eligible so a TAP
 * outage cannot show the modal to everyone.
 */
export function useRovoChatEligibility(
	userId: string | undefined,
	{ enabled = true, product, cloudId }: UseRovoChatEligibilityOptions,
): RovoChatEligibility {
	const [result, setResult] = useState<RovoChatEligibility>(DISABLED_RESULT);

	useEffect(() => {
		if (!enabled || !userId) {
			return;
		}

		let cancelled = false;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), TRAITS_FETCH_TIMEOUT_MS);

		const userTraitsPromise = fetchTraits(getUserTraitsUrl(userId), controller.signal);
		const siteTraitsPromise = cloudId
			? fetchTraits(getSiteUserTraitsUrl(cloudId, userId), controller.signal)
			: Promise.resolve<Trait[]>([]);

		Promise.all([userTraitsPromise, siteTraitsPromise])
			.then(([userTraits, siteTraits]) => {
				if (cancelled) {
					return;
				}
				const findValue = (traits: Trait[], name: string): TraitValue | undefined =>
					traits.find((t) => t.name === name)?.value;

				const hasSentMessage = isTraitTruthy(
					findValue(userTraits, TRAIT_HAS_SENT_ROVO_CHAT_MESSAGE),
				);
				const isRovoChatMau = isTraitTruthy(findValue(userTraits, TRAIT_IS_ROVO_CHAT_MAU));
				const qualifies = !hasSentMessage || !isRovoChatMau;

				// Exclude first-time users (covered by a separate onboarding
				// experience). Only applied when we can read the site-scoped
				// activation trait, which requires a cloudId.
				const firstTimeUser = cloudId
					? isFirstTimeUser(findValue(siteTraits, FIRST_ACTIVE_TRAIT_BY_PRODUCT[product]))
					: false;

				setResult({ loaded: true, eligible: qualifies && !firstTimeUser });
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				// Network error or timeout abort: fail closed (do not show the modal).
				setResult({ loaded: true, eligible: false });
			})
			.finally(() => {
				clearTimeout(timeoutId);
			});

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
			controller.abort();
		};
	}, [userId, enabled, product, cloudId]);

	return enabled ? result : DISABLED_RESULT;
}
